import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";
import { RouteGuardPermissions } from "@/components/permissions/route-guard-multiple";
import DocumentRecordForm from "@/components/document-records/document-record-form/document-record-form";
import type { DocumentRecordWithTemplate } from "@/hooks/documents/useDocumentRecordForm";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { FileText, ArrowLeft } from "lucide-react";

export const Route = createFileRoute(
  "/_authenticated/documents/records/$id/edit",
)({
  component: EditRecordRoute,
});

function EditRecordRoute() {
  const { id } = Route.useParams();
  const navigate = useNavigate();

  const isValidId = /^[a-z0-9]{30,40}$/.test(id);
  const record = useQuery(
    api.companies.documents.records.queries.getRecordById,
    isValidId ? { id: id as Id<"document_records"> } : "skip",
  );

  if (record === undefined) {
    return (
      <Card className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden shadow-sm p-2">
        <CardHeader className="p-0! flex justify-between items-center ">
          <div className="flex items-center p-0 ">
            <Skeleton className="h-5 w-1/3" />
          </div>
          <div className="p-0 flex gap-1 items-center">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-7 w-7" />
            <Skeleton className="h-7 w-7" />
            <Skeleton className="h-7 w-7" />
            <Skeleton className="h-7 w-24" />
            <Skeleton className="h-7 w-7" />
            <Skeleton className="h-7 w-7" />
            <Skeleton className="h-7 w-7" />
          </div>
        </CardHeader>
        <CardContent className="flex min-h-0 flex-1 flex-col space-y-2 px-0">
          <div className="flex min-h-0 flex-1 gap-2">
            <div className="flex min-h-0 flex-1 flex-col gap-2 rounded-md border border-border">
              <div className="flex items-center gap-2 border-b border-border/50 px-2 py-1.5">
                {Array.from({ length: 10 }).map((_, i) => (
                  <Skeleton key={`tb-${i}`} className="h-6 w-7" />
                ))}
              </div>
              <div className="flex-1 p-4">
                <Skeleton className="h-full w-full" />
              </div>
            </div>
            <div className="flex w-[260px] flex-col rounded-md border border-border bg-muted/20">
              <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-6 w-6" />
              </div>
              <div className="flex-1 space-y-2 p-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={`fld-${i}`} className="space-y-1.5">
                    <Skeleton className="h-3 w-1/3" />
                    <Skeleton className="h-8 w-full" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (record === null) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20">
        <div className="rounded-full bg-muted p-4">
          <FileText className="h-8 w-8 text-muted-foreground" />
        </div>
        <div className="text-center">
          <p className="text-base font-semibold">Documento no encontrado</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Este documento no existe o fue eliminado.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate({ to: "/documents" })}
        >
          <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
          Volver a Documentos
        </Button>
      </div>
    );
  }

  return (
    <RouteGuardPermissions
      permissions={[{ module: "document_records", actions: ["update"] }]}
    >
      <DocumentRecordForm
        key={record._id}
        record={record as unknown as DocumentRecordWithTemplate}
      />
    </RouteGuardPermissions>
  );
}
